import { submitCustomerSurvey } from '@/api/customerSurvey';
import { clearAllLocalStorage } from '@/lib/clearAllLocalStorage';
import { writeTngRewardSession } from '@/lib/tngRewardSession';
import MetallicPillButton from '@/components/Base/MetallicPillButton';
import SurveyBackgroundLayout from '@/components/layout/SurveyBackgroundLayout';
import { SurveyBrownPanel, SurveyBrownPanelInset } from '@/components/layout/SurveyBrownPanel';
import SurveyValidationAlert from '@/components/survey/SurveyValidationAlert';
import { isQ7_1Complete } from '@/lib/q7_1Storage';
import { isQ7_2Complete } from '@/lib/q7_2Storage';
import { isQ7_3Complete } from '@/lib/q7_3Storage';
import { getEffectiveQ7_4Answers, isQ7_4CompleteForSurvey } from '@/lib/q7_4Storage';
import { isQ7_5Complete, readQ7_5Answers } from '@/lib/q7_5Storage';
import { isQ7_6Complete, readQ7_6Answers } from '@/lib/q7_6Storage';
import { readQ7_1Answers } from '@/lib/q7_1Storage';
import { readQ7_2Answers } from '@/lib/q7_2Storage';
import { readQ7_3Answers } from '@/lib/q7_3Storage';
import { isDevRegComplete, readDevRegAnswers } from '@/lib/devregStorage';
import { readTradeInAnswers } from '@/lib/tradeinStorage';
import {
  readTradeInContactAnswers,
  writeTradeInContactAnswers,
} from '@/lib/tradeinContactStorage';
import {
  buildOtherBrandsAnswers,
  isOtherBrandsComplete,
  readOtherBrandsAnswers,
} from '@/lib/otherBrandsStorage';
import { readSmokerProfileAnswers, isSmokerProfileComplete } from '@/lib/smokerProfileStorage';
import { isValidEmail } from '@/utils/email';
import { useSurveyLanguage } from '@/hooks/useSurveyLanguage';
import { useTranslation } from '@/hooks/useTranslation';
import { useAppSelector } from '@/stores/hooks';
import { selectPdpaConsent, selectSurveyScreening, selectUniqueCodeDraft } from '@/stores/selectors';
import { formatLocalDigits, parseLocalMobileInput } from '@/utils/malaysiaMobile';
import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

const lightInputClassName =
  'min-w-0 w-full rounded-sm border border-slate-300/90 bg-white px-3 py-2.5 text-[16px] font-semibold text-slate-900 placeholder:font-normal placeholder:text-slate-400 outline-none ring-0 focus:border-slate-400 focus:ring-1 focus:ring-slate-300';

const mobileFieldShell =
  'flex min-w-0 w-full overflow-hidden rounded-sm border border-slate-300/90 bg-white focus-within:border-slate-400 focus-within:ring-1 focus-within:ring-slate-300';

const mobilePrefixClass =
  'flex shrink-0 items-center border-r border-slate-300/90 bg-slate-100 px-2.5 py-2.5 text-[16px] font-semibold tabular-nums text-slate-800';

const mobileLocalInputClass =
  'min-w-0 flex-1 border-0 bg-transparent px-3 py-2.5 text-[16px] font-semibold text-slate-900 placeholder:font-normal placeholder:text-slate-400 outline-none focus:ring-0 tabular-nums';

export default function TradeInContact() {
  const navigate = useNavigate();
  const { surveyLanguage } = useSurveyLanguage();
  const { t } = useTranslation();
  const surveyScreening = useAppSelector(selectSurveyScreening);
  const pdpaConsent = useAppSelector(selectPdpaConsent);
  const { mobileLocalDigits: draftMobile, customerUuid } = useAppSelector(selectUniqueCodeDraft);

  const stored = readTradeInContactAnswers();
  const smokerDraft = readSmokerProfileAnswers();
  const [mobileLocalDigits, setMobileLocalDigits] = useState(
    () => stored?.mobileLocalDigits ?? draftMobile
  );
  const [name, setName] = useState(() => stored?.name || smokerDraft?.name || '');
  const [email, setEmail] = useState(() => stored?.email || smokerDraft?.email || '');
  const [warning, setWarning] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = () => {
    setWarning(null);
    const digits = mobileLocalDigits.replace(/\D/g, '');
    if (digits.length === 0) {
      setWarning(t.tradeInContact.mobileWarning);
      return;
    }
    const trimmedName = name.trim();
    if (!trimmedName) {
      setWarning(t.tradeInContact.nameWarning);
      return;
    }
    const trimmedEmail = email.trim();
    if (!isValidEmail(trimmedEmail)) {
      setWarning(t.tradeInContact.emailWarning);
      return;
    }
    if (!customerUuid) {
      setWarning(t.tradeInContact.sessionExpiredWarning);
      return;
    }

    const smoker = readSmokerProfileAnswers();
    const otherBrandsRaw = readOtherBrandsAnswers();
    const otherBrands = otherBrandsRaw
      ? buildOtherBrandsAnswers(otherBrandsRaw.brand ?? '', otherBrandsRaw.variant ?? '')
      : null;
    const q7_1 = readQ7_1Answers();
    const q7_2 = readQ7_2Answers();
    const q7_3 = readQ7_3Answers();
    const q7_4 = getEffectiveQ7_4Answers();
    const q7_5 = readQ7_5Answers();
    const q7_6 = readQ7_6Answers();
    const devreg = readDevRegAnswers();
    const tradein = readTradeInAnswers();

    if (
      !smoker?.race?.trim() ||
      !smoker?.state?.trim() ||
      !otherBrands?.brand?.trim() ||
      !otherBrands.variant?.trim() ||
      !q7_1 ||
      !q7_2 ||
      !q7_3 ||
      !q7_4 ||
      !q7_5 ||
      !q7_6 ||
      !devreg ||
      !tradein
    ) {
      setWarning(t.tradeInContact.incompleteWarning);
      return;
    }

    setIsSubmitting(true);
    writeTradeInContactAnswers({
      mobileLocalDigits: digits,
      name: trimmedName,
      email: trimmedEmail,
    });

    void submitCustomerSurvey(customerUuid, {
      race: smoker.race,
      state: smoker.state,
      brand: otherBrands.brand,
      variant: otherBrands.variant,
      q7_1,
      q7_2,
      q7_3,
      q7_4,
      q7_5,
      q7_6,
      devreg,
      tradein,
      tradeInContact: {
        name: trimmedName,
        email: trimmedEmail,
        mobileLocalDigits: digits,
      },
    })
      .then((tng) => {
        if (tng != null) {
          writeTngRewardSession({
            pin: tng.pin,
            expirydate: tng.expirydate,
            value: tng.value,
          });
          clearAllLocalStorage();
        }
        navigate('/tng', { replace: true });
      })
      .catch((err: unknown) => {
        setWarning(err instanceof Error ? err.message : t.tradeInContact.saveError);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  if (surveyLanguage == null) {
    return <Navigate to="/" replace />;
  }
  if (surveyScreening == null) {
    return <Navigate to="/age_smoker" replace />;
  }
  if (!pdpaConsent) {
    return <Navigate to="/pdpa" replace />;
  }
  if (!draftMobile || draftMobile.replace(/\D/g, '').length === 0) {
    return <Navigate to="/uniquecode" replace />;
  }
  if (!customerUuid) {
    return <Navigate to="/uniquecode" replace />;
  }
  if (!isSmokerProfileComplete()) {
    return <Navigate to="/smokerprofile" replace />;
  }
  if (!isOtherBrandsComplete()) {
    return <Navigate to="/otherbrands" replace />;
  }
  if (!isQ7_1Complete()) {
    return <Navigate to="/7_1" replace />;
  }
  if (!isQ7_2Complete()) {
    return <Navigate to="/7_2" replace />;
  }
  if (!isQ7_3Complete()) {
    return <Navigate to="/7_3" replace />;
  }
  if (!isQ7_4CompleteForSurvey()) {
    return <Navigate to="/7_4" replace />;
  }
  if (!isQ7_5Complete()) {
    return <Navigate to="/7_5" replace />;
  }
  if (!isQ7_6Complete()) {
    return <Navigate to="/7_6" replace />;
  }
  if (!isDevRegComplete()) {
    return <Navigate to="/devreg" replace />;
  }
  if (readTradeInAnswers()?.tradeIn !== 'yes_help') {
    return <Navigate to="/tradein" replace />;
  }

  return (
    <SurveyBackgroundLayout>
      <div className="relative z-10 flex min-h-0 flex-1 flex-col justify-center overflow-hidden">
        <div className="mx-auto flex w-full max-w-full flex-col py-6">
          <SurveyBrownPanelInset>
            <SurveyBrownPanel className="!p-4">
              <p className="m-0 text-left text-[16px] font-medium leading-snug text-white/95 sm:text-[17px]">
                {t.tradeInContact.instruction}
              </p>
              <div className="mt-5 flex flex-col gap-4">
                <div className="grid min-w-0 grid-cols-[auto_minmax(0,1fr)] items-center gap-x-2 gap-y-4 sm:gap-x-3">
                  <label
                    htmlFor="tradein-mobile"
                    className="text-left text-[16px] font-medium text-white/95 sm:text-[17px]"
                  >
                    {t.tradeInContact.mobileLabel}
                  </label>
                  <div className={mobileFieldShell}>
                    <span className={mobilePrefixClass} aria-hidden>
                      +60
                    </span>
                    <input
                      id="tradein-mobile"
                      type="tel"
                      name="mobile"
                      inputMode="numeric"
                      autoComplete="tel-national"
                      placeholder="12 345 6789"
                      value={formatLocalDigits(mobileLocalDigits)}
                      onChange={(e) =>
                        setMobileLocalDigits(parseLocalMobileInput(e.target.value))
                      }
                      className={mobileLocalInputClass}
                    />
                  </div>
                  <label
                    htmlFor="tradein-name"
                    className="text-left text-[16px] font-medium text-white/95 sm:text-[17px]"
                  >
                    {t.tradeInContact.nameLabel}
                  </label>
                  <input
                    id="tradein-name"
                    type="text"
                    name="name"
                    autoComplete="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className={lightInputClassName}
                  />
                  <label
                    htmlFor="tradein-email"
                    className="text-left text-[16px] font-medium text-white/95 sm:text-[17px]"
                  >
                    {t.tradeInContact.emailLabel}
                  </label>
                  <input
                    id="tradein-email"
                    type="email"
                    name="email"
                    autoComplete="email"
                    inputMode="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={lightInputClassName}
                  />
                </div>
              </div>
              {warning != null ? (
                <div className="mt-4">
                  <SurveyValidationAlert>{warning}</SurveyValidationAlert>
                </div>
              ) : null}
              <div className="mt-8 flex justify-center">
                <MetallicPillButton
                  type="button"
                  size="sm"
                  className="metallic-pill--lang-home"
                  disabled={isSubmitting}
                  onClick={handleSubmit}
                >
                  {t.tradeInContact.submit}
                </MetallicPillButton>
              </div>
              <p className="mt-6 m-0 text-left text-[13px] italic leading-snug text-white/80 sm:text-[14px]">
                {t.tradeInContact.pdpaNote}
              </p>
            </SurveyBrownPanel>
          </SurveyBrownPanelInset>
        </div>
      </div>
    </SurveyBackgroundLayout>
  );
}
